import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { setTokens } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, ShieldCheck, Zap } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setTokens(data);
      const { access_token, refresh_token, ...profile } = data;
      setUser(profile);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-[#09090B] text-white p-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center">
            <BarChart3 className="h-5 w-5" aria-hidden="true" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">SND Commerce</span>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            One command center.<br />
            <span className="text-blue-500">Every channel.</span>
          </h1>
          <p className="text-zinc-400 text-base max-w-md leading-relaxed">
            Amazon, Flipkart and your WordPress store — sales, settlements, fees and prices in a single dashboard, queryable with Claude AI.
          </p>
          <div className="flex gap-8 pt-4">
            <div className="flex items-center gap-2 text-sm text-zinc-300"><Zap className="h-4 w-4 text-amber-500" aria-hidden="true" /> Live analytics</div>
            <div className="flex items-center gap-2 text-sm text-zinc-300"><ShieldCheck className="h-4 w-4 text-green-500" aria-hidden="true" /> MCP ready</div>
          </div>
        </div>
        <p className="relative z-10 text-xs text-zinc-500 tracking-[0.2em] uppercase">Multi-channel seller intelligence</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="space-y-2">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground">Use your company account to access the dashboard.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Username or email</Label>
            <Input id="email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="login-password-input" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit-button">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
